// ============================================================================
// CHANGE SETTLEMENT (in-house) — PMS Phase 2 slice 3b, agreed 2026-10-13
// ============================================================================
// Who pays on a stay that is in house or checked out but not yet invoiced
// (2026-10-16). Before check-in the mode is set on the booking form. "As party
// master" leaves the stay to the party's default. Invoiced lines never move — a
// credit note corrects those.
//
// "Apply to"
//   Whole stay (default) — the lines already posted that the hotel has not yet
//     invoiced move to the payer the new mode names. Amounts never change; each
//     moved line is audited with the reason. A restaurant bill keeps its own
//     invoice; its payer only decides who settles it.
//   From now on — only charges posted after the change follow the new mode, for
//     an agreement that genuinely starts mid-stay.
// Room nights not yet posted follow the new mode in either case.
//
// The preview is read from the server for the picked mode, so the dialog says
// exactly how many lines move to whom before anything changes.
//
// Lifecycle-modal pattern (as ChangeSourceModal): inline state reset on
// [isOpen, booking], no minimize, single submit.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import { bookingService } from 'api/services/bookingService';
import notificationService from 'services/notificationService';
import type { Booking } from 'api/types/booking.types';
import {
  FOLLOW_PARTY,
  type SettlementChoice,
  SETTLEMENT_MODE_OPTIONS,
  settlementModeLabel,
  type SettlementApplyTo,
  type SettlementChangePreview,
} from 'api/types/settlement.types';

interface ChangeSettlementModalProps {
  isOpen: boolean;
  booking: Booking | null;
  onClose: () => void;
  /** Fired after a successful change with the updated booking (the list refetches; Check-In updates its chip). */
  onChanged: (updated?: Booking) => void;
}

const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ChangeSettlementModal: React.FC<ChangeSettlementModalProps> = ({ isOpen, booking, onClose, onChanged }) => {
  const [mode, setMode] = useState<SettlementChoice>('GUEST');
  const [applyTo, setApplyTo] = useState<SettlementApplyTo>('WHOLE_STAY');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<SettlementChangePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || !booking) return;
    setMode(booking.settlementFollowsParty ? FOLLOW_PARTY : (booking.settlementMode || 'GUEST'));
    setApplyTo('WHOLE_STAY');
    setReason('');
    setError('');
    setPreview(null);
  }, [isOpen, booking]);

  // Re-read what the picked mode would move.
  useEffect(() => {
    if (!isOpen || !booking) return;
    let cancelled = false;
    setLoadingPreview(true);
    bookingService.previewSettlementChange(booking.bookingId, mode)
      .then((p) => { if (!cancelled) setPreview(p); })
      .catch((err: any) => { if (!cancelled) setError(err?.response?.data?.message || err?.message || 'Could not read the settlement.'); })
      .finally(() => { if (!cancelled) setLoadingPreview(false); });
    return () => { cancelled = true; };
  }, [isOpen, booking, mode]);

  const hasParty = !!preview?.partyType;
  const eligible = !!preview?.partyIsCreditEligible;
  const options = [
    ...(hasParty ? [{
      value: FOLLOW_PARTY as string,
      label: `As party master${preview?.partyDefaultMode ? ` (now ${settlementModeLabel(preview.partyDefaultMode)})` : ''}`,
      isDisabled: false,
    }] : []),
    ...SETTLEMENT_MODE_OPTIONS.map((o) => ({
      value: o.value as string,
      label: o.credit && preview && (!hasParty || !eligible)
        ? `${o.label} (${hasParty ? 'party not credit eligible' : 'no party on this booking'})`
        : o.label,
      isDisabled: o.credit && !!preview && (!hasParty || !eligible),
    })),
  ];
  const hint = mode === FOLLOW_PARTY
    ? "The stay follows the party master's settlement, now and if the master changes."
    : SETTLEMENT_MODE_OPTIONS.find((o) => o.value === mode)?.hint;
  const unchanged = preview
    ? (mode === FOLLOW_PARTY) === !!preview.currentFollowsParty && (mode === FOLLOW_PARTY || preview.currentMode === mode)
    : true;
  const moving = preview ? preview.linesToParty + preview.linesToGuest : 0;

  const handleSave = useCallback(async () => {
    if (!booking) return;
    if (unchanged && (applyTo === 'FROM_NOW' || moving === 0)) { setError('Nothing changes — pick a different settlement.'); return; }
    if (!reason.trim()) { setError('A reason is required to change settlement.'); return; }
    setError('');
    setSaving(true);
    try {
      const updated = await bookingService.changeSettlement(booking.bookingId, { settlementMode: mode, applyTo, reason: reason.trim() });
      notificationService.toast.success(`Settlement changed to ${settlementModeLabel(mode)} for "${booking.bookingNumber}".`);
      onChanged(updated);
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Settlement change failed.';
      notificationService.toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [booking, unchanged, applyTo, moving, reason, mode, onChanged, onClose]);

  if (!isOpen || !booking) return null;

  const partyName = preview?.partyName || booking.agentName || booking.corporateName || booking.otaDisplayName || 'the party';

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Change Settlement — {booking.bookingNumber}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.6rem' }}>
            Current: <b style={{ color: 'var(--text-primary)' }}>{settlementModeLabel(booking.settlementMode || 'GUEST')}</b>
            {booking.settlementFollowsParty && ' (as party master)'}
            {hasParty ? ` · ${partyName}` : ' · no agent, corporate or OTA'}
            <span style={{ display: 'block' }}>
              Changes who pays — amounts are never touched. Room nights not yet posted follow the new settlement.
            </span>
          </div>

          {error && <div className="form-error-message" style={{ marginBottom: '0.5rem' }}>{error}</div>}

          <div className="form-group">
            <SyutSelect
              label="New settlement"
              required
              options={options}
              value={mode}
              onChange={(v) => { setError(''); setMode(((v as string) || 'GUEST') as SettlementChoice); }}
              isClearable={false}
              isDisabled={saving}
              size="sm"
              menuPortalTarget={document.body}
              helperText={hint}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Apply to</label>
            {([
              { v: 'WHOLE_STAY', l: 'Whole stay', d: 'Charges already posted move to the new payer.' },
              { v: 'FROM_NOW', l: 'From now on', d: 'Only charges posted after this change.' },
            ] as Array<{ v: SettlementApplyTo; l: string; d: string }>).map((o) => (
              <label key={o.v} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.25rem' }}>
                <input
                  type="radio" name="settlement-apply-to" value={o.v}
                  checked={applyTo === o.v}
                  onChange={() => { setError(''); setApplyTo(o.v); }}
                  disabled={saving}
                  style={{ marginTop: '0.2rem', accentColor: 'var(--primary)' }}
                />
                <span style={{ fontSize: '0.85rem' }}>
                  {o.l} <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>— {o.d}</span>
                </span>
              </label>
            ))}
          </div>

          {/* What moves — read from the server for the picked mode */}
          <div style={{
            fontSize: '0.8rem', padding: '0.5rem 0.75rem', borderRadius: '6px', marginBottom: '0.75rem',
            background: 'var(--bg-secondary)', color: 'var(--text-secondary)',
          }}>
            {loadingPreview || !preview ? 'Checking the folio…'
              : applyTo === 'FROM_NOW'
                ? `Posted charges stay where they are${moving > 0 ? ` (${moving} line(s) would move under Whole stay)` : ''}.`
                : moving === 0
                  ? 'No posted charge changes payer.'
                  : (
                    <>
                      {preview.linesToParty > 0 && <div>{preview.linesToParty} line(s), {money(preview.amountToParty)} → {partyName}</div>}
                      {preview.linesToGuest > 0 && <div>{preview.linesToGuest} line(s), {money(preview.amountToGuest)} → the guest</div>}
                    </>
                  )}
            {preview && preview.linesLocked > 0 && (
              <div style={{ marginTop: '0.25rem' }}>{preview.linesLocked} line(s) already invoiced stay unchanged.</div>
            )}
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Reason <span className="text-danger">*</span></label>
            <textarea
              className="form-input form-textarea"
              rows={2} maxLength={300}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Corporate confirmed by email it will cover food — the audit anchor…"
              disabled={saving}
              style={{ fontSize: '0.8rem' }}
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || loadingPreview}>
            {saving ? 'Saving…' : 'Change Settlement'}
          </button>
        </div>
      </div>
    </>
  );
};

export default ChangeSettlementModal;
